/**
 * 캐시된 프로젝트 목록 조회 훅
 *
 * 온라인 상태: GraphQL 쿼리 후 캐시에 저장
 * 오프라인 상태: 캐시에서 데이터 조회
 */

import { useState, useEffect, useCallback } from "react";
import { useQuery } from "@apollo/client";
import { PROJECTS_QUERY } from "@/lib/graphql/projects";
import { syncCacheWithServer, getCachedProjects } from "@/lib/db/projects";
import { getCachedProjectIds } from "@/lib/db/machines";
import { useNetworkStatus } from "./useNetworkStatus";
import type { CachedProject } from "@/lib/db/types";

interface Project {
  _id: string;
  number?: string;
  name: string;
  companyId?: string;
  companyName: string;
  type?: string; // PERFORMANCE_CHECK | MAINTENANCE
  status: string;
  startDate: string;
  endDate: string;
  createdAt?: string;
  updatedAt?: string;
}

interface UseCachedProjectsResult {
  projects: CachedProject[];
  loading: boolean;
  error: Error | null;
  isFromCache: boolean;
  refetch: () => void;
  /** 설비 데이터가 캐시된 프로젝트 ID 집합 (오프라인 접근 가능 여부 판단용) */
  cachedProjectIds: Set<string>;
}

export function useCachedProjects(): UseCachedProjectsResult {
  const { isOffline, isLoading: networkLoading } = useNetworkStatus();
  const [cachedProjects, setCachedProjects] = useState<CachedProject[]>([]);
  const [isFromCache, setIsFromCache] = useState(false);
  const [cacheLoading, setCacheLoading] = useState(true);
  const [cacheLoadedOnce, setCacheLoadedOnce] = useState(false);
  const [cachedProjectIds, setCachedProjectIds] = useState<Set<string>>(new Set());

  // 캐시에서 먼저 로드 (항상 실행)
  useEffect(() => {
    async function loadCacheFirst() {
      if (cacheLoadedOnce) return;
      setCacheLoading(true);
      try {
        const cached = await getCachedProjects();
        if (cached.length > 0) {
          setCachedProjects(cached);
          setIsFromCache(true);
        }
        // 설비 데이터가 캐시된 프로젝트 ID 목록 로드
        const projectIdsWithMachines = await getCachedProjectIds();
        setCachedProjectIds(projectIdsWithMachines);
        setCacheLoadedOnce(true);
      } catch (err) {
        console.error("캐시 로드 실패:", err);
      } finally {
        setCacheLoading(false);
      }
    }
    loadCacheFirst();
  }, [cacheLoadedOnce]);

  // GraphQL 쿼리 (네트워크 상태 확인 완료 후, 온라인 시에만 실행)
  const {
    data,
    loading: queryLoading,
    error: queryError,
    refetch,
  } = useQuery(PROJECTS_QUERY, {
    variables: { page: 1, limit: 100 },
    skip: networkLoading || isOffline, // 네트워크 상태 로딩 중이면 skip
    fetchPolicy: "network-only", // 온라인이면 항상 서버에서 가져옴
  });

  // 온라인 데이터 수신 시 캐시에 저장 (삭제된 프로젝트 제거 포함)
  useEffect(() => {
    async function saveToCache() {
      if (!isOffline && data?.projects?.projects) {
        try {
          const projectsToCache = data.projects.projects.map((p: Project) => ({
            _id: p._id,
            number: p.number ?? "",
            name: p.name,
            companyId: p.companyId ?? "",
            companyName: p.companyName,
            type: p.type, // PERFORMANCE_CHECK | MAINTENANCE (오프라인 표시용)
            status: p.status,
            startDate: p.startDate,
            endDate: p.endDate,
            createdAt: p.createdAt ?? "",
            updatedAt: p.updatedAt ?? "",
          }));
          // 서버 데이터로 캐시 동기화 (삭제된 프로젝트 자동 제거)
          await syncCacheWithServer(projectsToCache);

          // 캐시된 데이터도 업데이트
          const cached = await getCachedProjects();
          setCachedProjects(cached);
          setIsFromCache(false);

          // 설비 데이터가 캐시된 프로젝트 ID 목록도 갱신
          const projectIdsWithMachines = await getCachedProjectIds();
          setCachedProjectIds(projectIdsWithMachines);
        } catch (err) {
          console.error("캐시 저장 실패:", err);
        }
      }
    }

    saveToCache();
  }, [isOffline, data]);

  // 결과 결정: 캐시 우선, 서버 데이터가 있으면 그것 사용
  const projects = cachedProjects;

  // 로딩: 캐시 로딩 중이거나 (온라인이고 쿼리 로딩 중이면서 캐시가 비었을 때)
  const loading = cacheLoading || (!isOffline && queryLoading && cachedProjects.length === 0);
  const error = isOffline ? null : (queryError ?? null);

  const handleRefetch = useCallback(() => {
    if (!isOffline) {
      refetch();
    }
  }, [isOffline, refetch]);

  return {
    projects,
    loading,
    error,
    isFromCache,
    refetch: handleRefetch,
    cachedProjectIds,
  };
}
